// =============================================================================
// client/src/components/flux/SnapshotNote.jsx | G4 stage 1 — the report's freshness label
// =============================================================================
// PRESENTATIONAL. The server derives every state (fresh, stale, pending) and sends it as
// `rollup.snapshot`; this only words it. A response with NO `snapshot` key is today's live
// computation: it renders nothing here, so the page is unchanged until the routes flip.
//
// `as_of` is the START of the refresh's read window, not a point-in-time cutoff, so it is labelled
// "data as of", never "updated at".
import { BODY_MUTED } from '../../lib/flux/board.js';

const asOfLabel = (iso) => new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

// "The report is being prepared" panel, for a missing or incompatible base (no tiers, no zeros).
export function PendingPanel({ kind = 'pending', errorClass = null }) {
  const text = kind === 'loading'
    ? 'Loading the report…'
    : kind === 'error'
      ? "The report couldn't be loaded. Reload the page to try again."
      : 'The report is being prepared. This page will update on its own.';
  return (
    <div role="status" className="rounded-2xl border-2 border-dashed border-[#0E2A20] px-5 py-4 text-sm text-[#0E2A20]">
      {text}
      {kind === 'pending' && errorClass ? <p className={`${BODY_MUTED} text-xs mt-2`}>The last attempt to build it didn&apos;t finish ({errorClass}); it will retry.</p> : null}
    </div>
  );
}

export default function SnapshotNote({ snapshot }) {
  if (!snapshot || snapshot.state === 'pending' || !snapshot.as_of) return null;
  const late = snapshot.state === 'stale';
  const tone = snapshot.warn
    ? 'border-amber-500 bg-amber-50 text-amber-900'
    : late ? 'border-[#0E2A20]/40 text-[#0E2A20]' : 'border-transparent text-[#5A5A5A]';
  return (
    <div role="status" className={`rounded-2xl border-2 px-5 py-2 text-xs ${tone}`}>
      Data as of {asOfLabel(snapshot.as_of)}
      {late ? ' — update delayed' : ''}
      {snapshot.failed && snapshot.error_class ? ` (the last refresh failed: ${snapshot.error_class})` : ''}
      {snapshot.warn ? ' — these numbers are more than 6 hours old.' : ''}
    </div>
  );
}
